/**
 * État interne du moteur entre deux années simulées. Construit depuis la baseline (jamais depuis des constantes).
 */
import { promoteV03 } from "./baseline-merge.js";
import { assertBaselineComplete, block, figure } from "./params.js";
import type { Baseline, Financing, Measure, Outlook, ParamSet, Profile, Programme, SimContent } from "./types.js";
import { param, paramOpt } from "./params.js";

/** Effet d'une option d'événement en vigueur depuis le tour `since`. */
export interface ActiveEventEffect {
  event_id: string;
  option_id: string;
  since: number;
  /** Vrai si l'issue défavorable (`option.risk`) a été tirée. */
  risk_hit?: boolean;
}

export interface EngineState {
  base_year: number;
  /** Nombre d'années déjà simulées (0 → la prochaine année simulée est la première du mandat). */
  turn: number;
  gdp: number;
  gpot_base: number;
  gpot: number;
  labour: number;
  unemployment: number;
  poverty: number;
  co2: number;
  /** Trajectoire tendancielle des émissions (Mt), hors mesures. */
  co2_trend: number;
  inflation: number;
  infl_persist: number;
  oat10: number;
  oat_persist: number;
  debt: number;
  debt_ratio: number;
  apparent_rate: number;
  last_impulse: number;
  /** Position budgétaire discrétionnaire de l'année précédente (Md€) : dépenses × multiplicateur − frein fiscal. */
  last_stance: number;
  trade: number;
  confidence: number;
  eu: number;
  cohesion: number;
  capital: number;
  below_three: number;
  lost_dividend: number;
  /** Stock de dette annulé depuis le début du mandat (Md€), pour les déclencheurs d'événements. */
  debt_cancelled: number;
  /** Années consécutives (année courante incluse) avec un rabot transversal non nul. */
  rabot_years: number;
  /** Facteur cumulé des effets `fx.co2` des mesures et événements (1 = tendanciel). */
  co2_measure_factor: number;
  /** Indice (1 = année de base) de la trajectoire tendancielle des crédits des missions. */
  missions_trend: number;
  /** Recettes tendancielles de l'État (Md€), suivent le PIB nominal. */
  revenue_trend: number;
  /** Autres dépenses de l'État (Md€), résidu comptable de l'année de base. */
  other_spend: number;
  /** Décalages cumulés des indices de service (rabot, événements) par mission. */
  svc_shift: Record<string, number>;
  service_index: Record<string, number>;
  /** Mesure → tour d'adoption (0 = adoptée avant la première année). */
  adopted: Record<string, number>;
  /** Valeur retenue du curseur d'hypothèse par mesure. */
  hyp: Record<string, number>;
  active_events: ActiveEventEffect[];
  /** Événements déjà déclenchés (chacun au plus une fois par mandat). */
  fired: string[];
  /** Majorité au Parlement (0–100, ADR 0005). */
  majority: number;
  /** Capital politique à la fin de l'année précédente (variation → majorité). */
  capital_mark: number;
  /** Indice de pouvoir d'achat (100 = tendanciel de l'année de base) et sa valeur l'année précédente. */
  purchasing_power: number;
  pp_prev: number;
  /** Perte de pouvoir d'achat due aux surprises d'inflation non encore rattrapées par les salaires (%). */
  pp_infl_loss: number;
  /** Écart de production (niveau de PIB vs potentiel, %), qui se referme au rythme `output_gap_closure` (ADR 0005 §4). */
  output_gap: number;
  /** Écart cumulé du niveau des prix dû aux chocs d'inflation macro (Σ (inflation − inflation de base)), en %. */
  price_level_gap: number;
  /** Niveau courant de l'effet `fx.u` des mesures (pt), pour appliquer `fx.u` comme un niveau (ADR 0005). */
  u_measure_level: number;
  /** Niveau courant de l'effet `fx.coh` des mesures (points de cohésion), appliqué comme un niveau (ADR 0008 §c). */
  coh_measure_level: number;
  /** Mesures retirées en cours de mandat (ex. annulation de dette réintégrée). */
  retired: string[];
  /** Solde APU de l'année précédente (% du PIB, négatif = déficit), pour la crédibilité de la trajectoire (ADR 0005). */
  deficit_prev: number;
}

export interface Resolved {
  measures: Measure[];
  byId: Record<string, Measure>;
}

/** Vue « nombres » de la baseline, construite une fois (toute valeur null → erreur). */
export interface BaselineView {
  base_year: number;
  missions: { id: string; label: string; credits: number; svc: number }[];
  missions_total: number;
  revenues_total: number;
  /** Produit de l'année de base de chaque ligne de recettes (Md€, id → montant) : assiette des leviers (fuite comportementale, ADR 0017). */
  revenue_yields: Record<string, number>;
  interest: number;
  balance: number;
  gdp: number;
  gpot: number;
  inflation: number;
  unemployment: number;
  poverty: number;
  co2: number;
  oat10: number;
  maturity: number;
  apparent_rate: number;
  debt: number;
  asso_balance: number;
  asso_trend: number[];
  apul_balance: number;
  apul_trend: number[];
  trade: number;
  /** Régénération annuelle du capital politique (`params.political_regen`, ADR 0005 : déplacée de la baseline vers params). */
  regen: number;
  /** Trajectoire de référence (`baseline.outlook`, ADR 0009 §6), séries alignées sur `years` ; null = comportement v0.2. */
  outlook: OutlookView | null;
  /** Point de départ (fin de l'année de base) : sans outlook, valeurs de la baseline telles quelles (v0.2). */
  start: StartPoint;
  /**
   * Calage du tendanciel sur la trajectoire officielle de la première année du mandat (% du PIB, ressource de l'État) : mesures
   * du PLF déjà présentées, que le jeu ne détaille pas. Calculé par `createGame` (0 sans outlook). ADR 0009 §6.
   */
  plf_anchor_pct: number;
  /**
   * Vrai si le compte de l'État vient des champs v0.3 du constructeur de données (`missions_v03`, `revenues_v03`, `trend_v03`) :
   * ADR 0014 §3. Faux = fixture / v0.2.
   */
  data_v03: boolean;
}

export interface OutlookView {
  years: number[];
  series: Record<string, (number | null)[]>;
}

/** Point de départ du mandat (fin de l'année de base). */
export interface StartPoint {
  /** Année représentée (année de base). */
  year: number;
  gdp: number;
  debt: number;
  /** Dette à la fin de l'année précédente (base du taux apparent de l'année de base). */
  debt_prev: number;
  inflation: number;
  oat10: number;
  apparent_rate: number;
  /** Solde APU de l'année de base (% du PIB, négatif = déficit). */
  deficit_pct: number;
  /** Solde de l'État de l'année de base (Md€) retenu pour le résidu « autres dépenses ». */
  state_balance: number;
  /** Croissance réelle de l'année de base (%), pour l'affichage du départ. */
  growth: number;
  /** Vrai si le départ vient de `baseline.outlook` (année de base exécutée selon la trajectoire). */
  from_outlook: boolean;
}

/**
 * Valeur d'une série de l'outlook pour une année : la valeur de l'année si renseignée, sinon la dernière valeur renseignée
 * avant elle (convention : prolongation, jamais d'extrapolation) ; au-delà de la dernière année, la dernière valeur renseignée.
 * `undefined` si la série manque ou si aucune valeur n'est connue jusque-là.
 */
export function outlookAt(o: OutlookView | null, key: string, year: number): number | undefined {
  if (!o) return undefined;
  const ser = o.series[key];
  if (!ser || o.years.length === 0) return undefined;
  const last = o.years[o.years.length - 1] ?? year;
  let idx = o.years.indexOf(year);
  if (idx < 0) {
    if (year > last) idx = o.years.length - 1;
    else return undefined;
  }
  for (let i = idx; i >= 0; i--) {
    const v = ser[i];
    if (v !== null && v !== undefined) return v;
  }
  return undefined;
}

const OUTLOOK_SERIES = [
  "inflation_pct",
  "oat10_pct",
  "nominal_gdp_growth_pct",
  "real_gdp_growth_pct",
  "apu_deficit_pct_gdp",
  "apu_debt_pct_gdp",
  "apu_interest_bn",
  "apu_interest_pct_gdp",
  "potential_growth_pct",
  "asso_balance_pct_gdp",
  "apul_balance_pct_gdp",
] as const;

function outlookView(o: Outlook | undefined): OutlookView | null {
  if (!o || !Array.isArray(o.years) || o.years.length === 0) return null;
  const series: Record<string, (number | null)[]> = {};
  for (const k of OUTLOOK_SERIES) {
    const arr = o[k];
    if (!arr) continue;
    if (arr.length !== o.years.length) throw new Error(`baseline.outlook.${k} : ${arr.length} valeurs pour ${o.years.length} années`);
    // Élément null (année non publiée, v03-data) ou figure `TODO` à value null (stub) : même sens, valeur inconnue.
    series[k] = arr.map((f) => (f === null || f === undefined ? null : f.value));
  }
  return { years: [...o.years], series };
}

/** Croissance nominale de l'année `y` selon l'outlook (nominale, sinon réelle × inflation), en %. */
export function outlookNominalGrowth(o: OutlookView | null, y: number): number | undefined {
  const nom = outlookAt(o, "nominal_gdp_growth_pct", y);
  if (nom !== undefined) return nom;
  const real = outlookAt(o, "real_gdp_growth_pct", y);
  const infl = outlookAt(o, "inflation_pct", y);
  if (real === undefined || infl === undefined) return undefined;
  return ((1 + real / PCT) * (1 + infl / PCT) - 1) * PCT;
}

export function viewBaseline(baseline0: Baseline, params: ParamSet): BaselineView {
  assertBaselineComplete(baseline0);
  // v0.3 (ADR 0014 §3) : groupes de missions = Σ des missions LOLF officielles, recettes nettes 2026, soldes ASSO / APUL
  // `trend_v03` (bascule idempotente, déjà faite par `mergeBaseline`).
  const pr = promoteV03(baseline0);
  const baseline = pr.baseline;
  const dataV03 = pr.promoted.length > 0 || (baseline0.state.missions_v03?.length ?? 0) > 0;
  const missionsSrc = baseline.state.missions;
  const revenuesSrc = baseline.state.revenues;
  const missions = missionsSrc.map((m) => ({
    id: m.id,
    label: m.label,
    credits: figure(m.credits, `missions.${m.id}.credits`),
    svc: m.service_index ? figure(m.service_index, `missions.${m.id}.service_index`) : param(params, "svc_index_default"),
  }));
  const outlook = outlookView(baseline.outlook);
  const trendOf = (b: Baseline["asso"], name: string): number[] => b.trend.map((f, i) => figure(f, `${name}.trend[${i}]`));
  const base = {
    base_year: baseline.meta.base_year,
    missions,
    missions_total: missions.reduce((a, m) => a + m.credits, 0),
    revenues_total: revenuesSrc.reduce((a, r) => a + figure(r.amount, `revenues.${r.id}.amount`), 0),
    revenue_yields: Object.fromEntries(revenuesSrc.map((r) => [r.id, figure(r.amount, `revenues.${r.id}.amount`)])),
    interest: figure(baseline.state.debt_interest, "state.debt_interest"),
    balance: figure(baseline.state.balance, "state.balance"),
    gdp: block(baseline.macro, "gdp_nominal", "macro"),
    gpot: block(baseline.macro, "potential_growth", "macro"),
    inflation: block(baseline.macro, "inflation", "macro"),
    unemployment: block(baseline.macro, "unemployment_rate", "macro"),
    poverty: block(baseline.macro, "poverty_rate", "macro"),
    co2: block(baseline.macro, "ghg_emissions", "macro"),
    oat10: block(baseline.apu, "marginal_rate_oat10", "apu"),
    maturity: block(baseline.apu, "average_maturity_years", "apu"),
    apparent_rate: block(baseline.apu, "apparent_rate", "apu"),
    debt: block(baseline.apu, "debt_bn", "apu"),
    asso_balance: figure(baseline.asso.balance, "asso.balance"),
    asso_trend: trendOf(baseline.asso, "asso"),
    apul_balance: figure(baseline.apul.balance, "apul.balance"),
    apul_trend: trendOf(baseline.apul, "apul"),
    trade: block(baseline.trade, "balance_bn", "trade"),
    regen: param(params, "political_regen"),
  };
  return { ...base, outlook, start: startPoint(baseline, base, outlook), plf_anchor_pct: 0, data_v03: dataV03 };
}

/**
 * Point de départ (ADR 0009 §6). Avec un outlook couvrant l'année de base : PIB de l'année de base = PIB publié (année du chiffre)
 * × croissance nominale de l'outlook jusqu'à l'année de base ; dette = ratio de l'outlook × PIB ; taux apparent = charge
 * d'intérêts APU de l'année de base / dette de fin d'année précédente ; inflation, OAT et solde APU = valeurs de l'outlook.
 * Sans outlook : les chiffres de la baseline tels quels (comportement v0.2, l'année de base est « sautée »).
 */
function startPoint(baseline: Baseline, v: Omit<BaselineView, "outlook" | "start" | "plf_anchor_pct" | "data_v03">, o: OutlookView | null): StartPoint {
  const y0 = v.base_year;
  const v2: StartPoint = {
    year: y0,
    gdp: v.gdp,
    debt: v.debt,
    debt_prev: v.debt,
    inflation: v.inflation,
    oat10: v.oat10,
    apparent_rate: v.apparent_rate,
    deficit_pct: ((v.balance + v.asso_balance + v.apul_balance) / v.gdp) * PCT,
    state_balance: v.balance,
    growth: paramFree(baseline.macro["gdp_growth"]?.value) ?? v.gpot,
    from_outlook: false,
  };
  if (!o || !o.years.includes(y0)) return v2;
  const gdpYear = baseline.macro["gdp_nominal"]?.year ?? y0;
  let gdp = v.gdp;
  for (let y = gdpYear + 1; y <= y0; y++) {
    const nom = outlookNominalGrowth(o, y);
    if (nom === undefined) return v2;
    gdp *= 1 + nom / PCT;
  }
  const debtYear = baseline.apu["debt_bn"]?.year ?? y0;
  const debtPct = outlookAt(o, "apu_debt_pct_gdp", y0);
  const debt = debtPct !== undefined ? (debtPct * gdp) / PCT : v.debt;
  const debtPrev = debtYear < y0 ? v.debt : debt;
  const interest = outlookAt(o, "apu_interest_bn", y0) ?? mul(outlookAt(o, "apu_interest_pct_gdp", y0), gdp / PCT);
  const deficitPct = outlookAt(o, "apu_deficit_pct_gdp", y0) ?? v2.deficit_pct;
  const apuBal = (deficitPct * gdp) / PCT;
  // Soldes ASSO / APUL de l'année de base : trajectoire officielle (% du PIB) si publiée, sinon soldes de la baseline (v0.2).
  const assoPct = outlookAt(o, "asso_balance_pct_gdp", y0);
  const apulPct = outlookAt(o, "apul_balance_pct_gdp", y0);
  const asso0 = assoPct !== undefined ? (assoPct * gdp) / PCT : v.asso_balance;
  const apul0 = apulPct !== undefined ? (apulPct * gdp) / PCT : v.apul_balance;
  return {
    year: y0,
    gdp,
    debt,
    debt_prev: debtPrev,
    inflation: outlookAt(o, "inflation_pct", y0) ?? v.inflation,
    oat10: outlookAt(o, "oat10_pct", y0) ?? v.oat10,
    apparent_rate: interest !== undefined ? (interest / debtPrev) * PCT : v.apparent_rate,
    deficit_pct: deficitPct,
    // Solde de l'État de l'année de base (au sens de la comptabilité nationale, ODAC compris) : solde APU de l'outlook − soldes
    // ASSO/APUL de l'année de base (outlook, sinon baseline ; blocs exogènes v1).
    state_balance: apuBal - asso0 - apul0,
    growth: outlookAt(o, "real_gdp_growth_pct", y0) ?? v2.growth,
    from_outlook: true,
  };
}

const mul = (a: number | undefined, b: number): number | undefined => (a === undefined ? undefined : a * b);
const paramFree = (x: number | null | undefined): number | undefined => (x === null || x === undefined ? undefined : x);

/** Solde tendanciel d'un bloc exogène l'année k (0-based) : trend[k], dernière valeur prolongée, sinon solde de base. */
export function trendAt(trend: number[], fallback: number, k: number): number {
  if (trend.length === 0) return fallback;
  return trend[Math.min(k, trend.length - 1)] ?? fallback;
}

/** Forme minimale d'une variante (le type complet `MeasureVariant` vient du contenu v0.2). */
interface VariantLike {
  id: string;
  scale: number;
  cost?: Profile;
  cost_low?: Profile;
  cost_high?: Profile;
}

const scaleProfile = (p: Profile, k: number): Profile => (Array.isArray(p) ? p.map((x) => x * k) : p * k);

/**
 * Mesure effective pour une variante (ADR 0005 §13) : coût = `variant.cost` (recalculé par le contenu depuis les coûts
 * unitaires de la décomposition) ou, à défaut, coût × `scale` ; effets `fx` et coût politique × `scale` (linéaire).
 */
export function applyVariant(m: Measure, variantId: string): Measure {
  const v = ((m as Measure & { variants?: VariantLike[] }).variants ?? []).find((x) => x.id === variantId);
  if (!v) throw new Error(`programme : variante « ${variantId} » inconnue pour la mesure « ${m.id} »`);
  const k = v.scale;
  const fx: Record<string, unknown> = {};
  for (const [key, p] of Object.entries(m.fx)) {
    if (p === undefined) continue;
    if (typeof p === "number" || Array.isArray(p)) fx[key] = scaleProfile(p, k);
    else fx[key] = Object.fromEntries(Object.entries(p as Record<string, Profile>).map(([sub, q]) => [sub, scaleProfile(q, k)]));
  }
  const out: Measure = { ...m, cost: v.cost ?? scaleProfile(m.cost, k), political_cost: m.political_cost * k, fx: fx as Measure["fx"] };
  const low = v.cost_low ?? (m.cost_low !== undefined ? scaleProfile(m.cost_low, k) : undefined);
  const high = v.cost_high ?? (m.cost_high !== undefined ? scaleProfile(m.cost_high, k) : undefined);
  if (low !== undefined) out.cost_low = low;
  if (high !== undefined) out.cost_high = high;
  return out;
}

/** Résout les mesures du programme dans le catalogue (variantes appliquées) ; vérifie exclusions et identifiants. */
export function resolveMeasures(programme: Programme, content: SimContent | undefined): Resolved {
  const catalogue = content?.measures ?? [];
  const byId: Record<string, Measure> = {};
  for (const m of catalogue) byId[m.id] = m;
  const measures: Measure[] = [];
  const chosen = new Set(programme.measure_ids);
  for (const id of programme.measure_ids) {
    const m = byId[id];
    if (!m) {
      throw new Error(
        content ? `programme : mesure inconnue « ${id} » dans le catalogue` : `programme : mesure « ${id} » mais aucun catalogue fourni (7e argument de simulate)`,
      );
    }
    for (const x of m.exclusive_with) if (chosen.has(x)) throw new Error(`programme : mesures incompatibles « ${id} » et « ${x} »`);
    const variant = programme.variants?.[id];
    measures.push(variant !== undefined ? applyVariant(m, variant) : m);
  }
  return { measures, byId };
}

export function initState(baseline: Baseline, params: ParamSet, programme: Programme, resolved: Resolved): EngineState {
  const v = viewBaseline(baseline, params);
  // Résidu comptable : recettes − missions − intérêts − autres = solde ⇒ autres = recettes − missions − intérêts − solde.
  // Intérêts de l'État = part État (`state_share_of_apu_interest`) de la charge APU modélisée (dette × taux apparent) : les
  // soldes ASSO/APUL tendanciels portent déjà leurs propres intérêts (ADR 0005 §2, plus de double compte).
  const st = v.start;
  const other = v.revenues_total - v.missions_total - stateInterest(params, st.debt_prev, st.apparent_rate) - st.state_balance;
  const svc: Record<string, number> = {};
  const shift: Record<string, number> = {};
  for (const m of v.missions) {
    svc[m.id] = m.svc;
    shift[m.id] = 0;
  }
  const hyp: Record<string, number> = {};
  const adopted: Record<string, number> = {};
  for (const m of resolved.measures) {
    adopted[m.id] = 0;
    if (m.hyp) {
      const h = programme.hyp[m.id] ?? m.hyp.default;
      if (h < m.hyp.low || h > m.hyp.high) throw new Error(`programme : hypothèse « ${m.id} » = ${h} hors de [${m.hyp.low}, ${m.hyp.high}]`);
      hyp[m.id] = h;
    }
  }
  return {
    base_year: v.base_year,
    turn: 0,
    gdp: st.gdp,
    gpot_base: v.gpot,
    gpot: v.gpot,
    labour: 1,
    unemployment: v.unemployment,
    poverty: v.poverty,
    co2: v.co2,
    co2_trend: v.co2,
    inflation: st.inflation,
    infl_persist: 0,
    oat10: st.oat10,
    oat_persist: 0,
    debt: st.debt,
    debt_ratio: (st.debt / st.gdp) * PCT,
    apparent_rate: st.apparent_rate,
    last_impulse: 0,
    last_stance: 0,
    trade: v.trade,
    confidence: block(baseline.politics, "confidence", "politics"),
    eu: block(baseline.politics, "eu_relation", "politics"),
    cohesion: block(baseline.politics, "cohesion", "politics"),
    capital: block(baseline.politics, "political_capital", "politics"),
    below_three: 0,
    lost_dividend: 0,
    debt_cancelled: 0,
    rabot_years: 0,
    co2_measure_factor: 1,
    missions_trend: 1,
    revenue_trend: v.revenues_total,
    other_spend: other,
    svc_shift: shift,
    service_index: svc,
    adopted,
    hyp,
    active_events: [],
    fired: [],
    majority: param(params, "majority_initial"),
    capital_mark: block(baseline.politics, "political_capital", "politics"),
    purchasing_power: PCT,
    pp_prev: PCT,
    pp_infl_loss: 0,
    output_gap: 0,
    price_level_gap: 0,
    u_measure_level: 0,
    coh_measure_level: 0,
    retired: [],
    deficit_prev: st.deficit_pct,
  };
}

/** Charge d'intérêts portée par le compte de l'État (Md€) : part État × dette APU × taux apparent. */
export function stateInterest(params: ParamSet, debtApu: number, apparentRate: number): number {
  return (param(params, "state_share_of_apu_interest") * debtApu * apparentRate) / PCT;
}

/** Conversion fraction ↔ pourcentage (unité, pas un paramètre économique). */
export const PCT = 100;

export function cloneState(s: EngineState): EngineState {
  return {
    ...s,
    svc_shift: { ...s.svc_shift },
    service_index: { ...s.service_index },
    adopted: { ...s.adopted },
    hyp: { ...s.hyp },
    active_events: s.active_events.map((e) => ({ ...e })),
    fired: [...s.fired],
    retired: [...s.retired],
  };
}

/** Valide la structure du financement (bornes des leviers, du rabot et des économies ciblées). */
export function validateFinancing(params: ParamSet, financing: Financing): void {
  for (const [id, pct] of Object.entries(financing.mission_cuts ?? {})) {
    const max = paramOpt(params, `cut_${id}_max_pct`);
    if (max === undefined) throw new Error(`financing : économie ciblée sur « ${id} » sans paramètre cut_${id}_max_pct`);
    // Négatif = hausse des crédits du groupe (levier symétrique, ADR 0009 §2).
    if (!(pct >= -max && pct <= max)) throw new Error(`financing : économie ciblée « ${id} » = ${pct} % hors de [${-max}, ${max}]`);
  }
  if (financing.across_the_board_cut < 0 || financing.across_the_board_cut > param(params, "cut_max_share")) {
    throw new Error(`financing : rabot ${financing.across_the_board_cut} hors de [0, ${param(params, "cut_max_share")}]`);
  }
  for (const [id, raise] of Object.entries(financing.revenue_raises)) {
    const min = param(params, `lev_${id}_min_bn`);
    const max = param(params, `lev_${id}_max_bn`);
    if (raise < min || raise > max) throw new Error(`financing : levier « ${id} » = ${raise} Md€ hors de [${min}, ${max}]`);
  }
}
